// react
import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import ReactTable from "react-table";
import moment from "moment";
import Datetime from "react-datetime";
import Select from 'react-select';
import SweetAlert from "react-bootstrap-sweetalert";

// @material-ui/icons
import Announcement from "@material-ui/icons/Announcement";
import Edit from "@material-ui/icons/Edit";
import ListAlt from "@material-ui/icons/ListAlt";
import Block from "@material-ui/icons/Block";

// @material-ui components
import Tooltip from "@material-ui/core/Tooltip";
import FormControl from "@material-ui/core/FormControl";
import FormHelperText from "@material-ui/core/FormHelperText";

// core components
import GridContainer from "beaktShared/components/Grid/GridContainer.js";
import GridItem from "beaktShared/components/Grid/GridItem.js";
import Button from "beaktShared/components/CustomButtons/Button.js";
import Card from "beaktShared/components/Card/Card.js";
import CardBody from "beaktShared/components/Card/CardBody.js";
import CardIcon from "beaktShared/components/Card/CardIcon.js";
import CardHeader from "beaktShared/components/Card/CardHeader.js";
import CustomLinearProgress from "beaktShared/components/CustomLinearProgress/CustomLinearProgress.js";
import Snackbar from "beaktShared/components/Snackbar/Snackbar.js";
import ComunicadoForm from "./ComunicadoForm";
import ComunicadoLogAceite from "./ComunicadoLogAceite";

// services
import * as ComunicadoService from "./ComunicadoService";
import { formateDate, getLocale, getStringFormatDate } from "beaktShared/helpers/masks";
import { ValidSelect } from "beaktShared/helpers/validselect";
import { noCaseSensitive } from 'beaktShared/helpers/extras';
import { SessionContext } from "beaktShared/services/session";

// styles
import { makeStyles } from "@material-ui/core/styles";
import styles from "assets/jss/material-dashboard-pro-react/views/crudeStyle.js";
import { successColor, dangerColor, warningColor, infoColor, grayColor } from "assets/jss/material-dashboard-pro-react.js";

const { TipoComunicado, SituacaoComunicado } = ComunicadoService;

const badge = (color) => ({
    display: "inline-block",
    padding: "2px 10px",
    borderRadius: "12px",
    fontSize: "12px",
    fontWeight: 500,
    color: "#fff",
    backgroundColor: color,
    whiteSpace: "nowrap"
});

const useStyles = makeStyles({
    ...styles,
    badgeAlerta: badge(dangerColor[0]),
    badgeAviso: badge(warningColor[0]),
    badgeInformativo: badge(infoColor[0]),
    badgeAtivo: badge(successColor[0]),
    badgeInativo: badge(grayColor[0]),
    badgeRascunho: badge(warningColor[0]),
    adesaoTexto: {
        fontSize: "12px",
        whiteSpace: "nowrap"
    }
});

const MAX_MESES_PESQUISA = 3;
const FORMATO_DATA_API = "YYYY-MM-DD";

const VIEW_LISTA = "lista";
const VIEW_FORM = "form";
const VIEW_LOG = "log";

export default function ComunicadoList({ callUpdateScrollTOP }) {
    const { t } = useTranslation();
    const classes = useStyles();
    const session = useContext(SessionContext);

    const tipoOptions = [
        { value: TipoComunicado.AvisoSistema, label: t('notice_board.type_system_notice') },
        { value: TipoComunicado.InformativoUnidade, label: t('notice_board.type_unit_information') },
        { value: TipoComunicado.Alerta, label: t('notice_board.type_alert') }
    ];

    const situacaoOptions = [
        { value: SituacaoComunicado.Ativo, label: t('label.active') },
        { value: SituacaoComunicado.Inativo, label: t('label.inactive') },
        { value: SituacaoComunicado.Pendente, label: t('notice_board.situation_draft') }
    ];

    const [notification, setNotification] = useState({ show: false, color: 'info', message: '' });
    const showNotification = (message, color) => {
        setNotification({ show: true, color: color, message: message });
        setTimeout(() => setNotification({ show: false }), 6000);
    };

    // ************************** FILTROS **************************
    const [dtini, setdtini] = useState(moment().subtract(1, 'months').startOf('day'));
    const [dtfim, setdtfim] = useState(moment().startOf('day'));
    const [tipo, setTipo] = useState(null);
    const [situacao, setSituacao] = useState(null);
    const [filterErrors, setFilterErrors] = useState({});

    // ************************** LISTAGEM **************************
    const [list, setList] = useState([]);
    const [isLoadingList, setIsLoadingList] = useState(false);
    const [isErrorList, setIsErrorList] = useState(false);
    const [ultimoFiltro, setUltimoFiltro] = useState(null);

    const loadList = (filtro) => {
        setUltimoFiltro(filtro);
        setIsLoadingList(true);
        setIsErrorList(false);

        ComunicadoService.listar(filtro).then(result => {
            setIsLoadingList(false);
            if (!result.ok) {
                setIsErrorList(true);
                setList([]);
                if (result.message)
                    showNotification(result.message, 'danger');
                return;
            }
            setList(result.data || []);
        });
    };

    const montarFiltro = () => ({
        franquiaId: session.franquiaId,
        dataIni: dtini.format(FORMATO_DATA_API),
        dataFim: dtfim.format(FORMATO_DATA_API),
        tipo: tipo,
        situacao: situacao
    });

    useEffect(() => {
        loadList(montarFiltro());
    // eslint-disable-next-line
    }, []);

    const isDataValida = (data) => moment.isMoment(data) && data.isValid();

    const handleClickPesquisa = () => {
        const errors = {};
        if (!isDataValida(dtini))
            errors.dtini = t('error_message.invalid_date');
        if (!isDataValida(dtfim))
            errors.dtfim = t('error_message.invalid_date');
        setFilterErrors(errors);
        if (Object.keys(errors).length > 0)
            return;

        if (dtini.isAfter(dtfim)) {
            showNotification(t('notice_board.start_date_after_end_date'), 'danger');
            return;
        }

        if (dtfim.diff(dtini, 'months', true) > MAX_MESES_PESQUISA) {
            showNotification(t('error_message.select_3month'), 'danger');
            return;
        }

        loadList(montarFiltro());
    };

    // ************************** NAVEGACAO FORM / LOG **************************
    const [view, setView] = useState(VIEW_LISTA);
    const [item, setItem] = useState({});

    // Toda troca de tela (lista / form / log) comeca do topo da rolagem
    useEffect(() => {
        if (callUpdateScrollTOP)
            callUpdateScrollTOP();
    // eslint-disable-next-line
    }, [view]);

    const handleNovo = () => {
        setItem({});
        setView(VIEW_FORM);
    };

    const handleEdit = row => {
        setItem(row);
        setView(VIEW_FORM);
    };

    const handleLog = row => {
        setItem(row);
        setView(VIEW_LOG);
    };

    const handleVoltarLista = () => {
        setItem({});
        setView(VIEW_LISTA);
        if (ultimoFiltro)
            loadList(ultimoFiltro);
    };

    const handleSalvo = (message) => {
        handleVoltarLista();
        showNotification(message, 'success');
        ComunicadoService.notificarComunicadosAtualizados();
    };

    // ************************** INATIVACAO LOGICA **************************
    const [alertInativar, setAlertInativar] = useState(null);
    const [isInativando, setIsInativando] = useState(false);

    const confirmarInativacao = (comunicado) => {
        setIsInativando(true);
        ComunicadoService.inativar(comunicado.comunicadoId).then(result => {
            setIsInativando(false);
            setAlertInativar(null);
            if (!result.ok) {
                if (result.message)
                    showNotification(result.message, 'danger');
                return;
            }
            showNotification(t('notice_board.inactivated_successfully'), 'success');
            loadList(ultimoFiltro);
            ComunicadoService.notificarComunicadosAtualizados();
        });
    };

    const showAlertInativar = (comunicado) => {
        setAlertInativar(comunicado);
    };

    const renderAlertInativar = () => {
        if (!alertInativar)
            return null;
        return (
            <SweetAlert
                warning
                style={{ display: "block", marginTop: "-200px" }}
                title={t('notice_board.confirm_inactivate_title')}
                onConfirm={() => { if (!isInativando) confirmarInativacao(alertInativar); }}
                onCancel={() => { if (!isInativando) setAlertInativar(null); }}
                confirmBtnCssClass={classes.button + " " + classes.success}
                cancelBtnCssClass={classes.button + " " + classes.danger}
                confirmBtnText={t('notice_board.confirm_inactivate_button')}
                cancelBtnText={t('button.cancel')}
                showCancel
                disabled={isInativando}
            >
                <b>{alertInativar.titulo}</b>
                <br />
                {t('notice_board.confirm_inactivate_message')}
            </SweetAlert>
        );
    };

    // ************************** COLUNAS **************************
    const renderTipo = (valor) => {
        switch (valor) {
            case TipoComunicado.AvisoSistema:
                return <span className={classes.badgeAviso}>{t('notice_board.type_system_notice')}</span>;
            case TipoComunicado.InformativoUnidade:
                return <span className={classes.badgeInformativo}>{t('notice_board.type_unit_information')}</span>;
            case TipoComunicado.Alerta:
                return <span className={classes.badgeAlerta}>{t('notice_board.type_alert')}</span>;
            default:
                return <span>-</span>;
        }
    };

    const renderSituacao = (valor) => {
        switch (valor) {
            case SituacaoComunicado.Ativo:
                return <span className={classes.badgeAtivo}>{t('label.active')}</span>;
            case SituacaoComunicado.Inativo:
                return <span className={classes.badgeInativo}>{t('label.inactive')}</span>;
            case SituacaoComunicado.Pendente:
                return <span className={classes.badgeRascunho}>{t('notice_board.situation_draft')}</span>;
            default:
                return <span>-</span>;
        }
    };

    const renderAdesao = (comunicado) => {
        // Rascunho ainda nao foi publicado e, portanto, nao possui destinatarios aguardando aceite
        if (comunicado.situacao === SituacaoComunicado.Pendente)
            return <span>-</span>;

        const total = comunicado.totalDestinatarios || 0;
        const aceites = comunicado.totalAceites || 0;
        const percentual = total > 0 ? Math.round((aceites / total) * 100) : 0;

        return (
            <div>
                <CustomLinearProgress
                    variant="determinate"
                    color={percentual === 100 ? "success" : "info"}
                    value={percentual}
                    style={{ marginBottom: "4px" }}
                />
                <span className={classes.adesaoTexto}>{aceites}/{total} ({percentual}%)</span>
            </div>
        );
    };

    const columns = [
        {
            Header: t('label.title'),
            accessor: "titulo"
        },
        {
            Header: t('notice_board.type'),
            accessor: "tipo",
            Cell: (props) => renderTipo(props.value),
            filterable: false,
            width: 190
        },
        {
            Header: t('notice_board.published'),
            accessor: "dataCriacao",
            Cell: (props) => props.original.situacao === SituacaoComunicado.Pendente
                ? <span>-</span>
                : <span>{formateDate(props.value)}</span>,
            filterable: false,
            width: 120
        },
        {
            Header: t('notice_board.adhesion'),
            id: "adesao",
            accessor: (row) => row.totalDestinatarios > 0 ? row.totalAceites / row.totalDestinatarios : 0,
            Cell: (props) => renderAdesao(props.original),
            filterable: false,
            width: 170
        },
        {
            Header: t('notice_board.situation'),
            accessor: "situacao",
            Cell: (props) => renderSituacao(props.value),
            filterable: false,
            width: 110
        },
        {
            Header: t('label.actions'),
            id: "acoes",
            Cell: row => {
                const inativo = row.original.situacao === SituacaoComunicado.Inativo;
                return (
                    <div className="actions-right">
                        {!inativo &&
                            <Button justIcon round simple color="success" className="edit"
                                onClick={() => handleEdit(row.original)}>
                                <Tooltip title={t('label.to_edit')} placement="top" classes={{ tooltip: classes.tooltip }}>
                                    <Edit />
                                </Tooltip>
                            </Button>}
                        {" "}
                        <Button justIcon round simple color="info" className="info"
                            onClick={() => handleLog(row.original)}>
                            <Tooltip title={t('notice_board.read_log')} placement="top" classes={{ tooltip: classes.tooltip }}>
                                <ListAlt />
                            </Tooltip>
                        </Button>
                        {" "}
                        {!inativo &&
                            <Button justIcon round simple color="danger" className="remove"
                                onClick={() => showAlertInativar(row.original)}>
                                <Tooltip title={t('notice_board.inactivate')} placement="top" classes={{ tooltip: classes.tooltip }}>
                                    <Block />
                                </Tooltip>
                            </Button>}
                    </div>
                );
            },
            sortable: false,
            filterable: false,
            width: 150
        }
    ];

    if (view === VIEW_FORM)
        return <ComunicadoForm item={item} addItem={handleSalvo} handleView={handleVoltarLista} />;

    if (view === VIEW_LOG)
        return <ComunicadoLogAceite comunicado={item} handleView={handleVoltarLista} />;

    return (
        <div>
            {notification.show &&
                <Snackbar
                    place="tr"
                    color={notification.color}
                    message={notification.message}
                    open={notification.show}
                    closeNotification={() => setNotification({ show: false })}
                    close
                />}

            <GridContainer>
                <GridItem xs={12}>
                    <Card>
                        <CardHeader color="primary" icon>
                            <CardIcon color="primary">
                                <Announcement />
                            </CardIcon>
                            <h4 className={classes.cardIconTitle}>{t('notice_board.title')}</h4>
                        </CardHeader>
                        <CardBody>
                            <GridContainer>
                                <GridItem xs={12} sm={6} md={2}>
                                    <FormControl fullWidth className={classes.formControl}>
                                        <label className={filterErrors.dtini ? "select__label select__label_error" : "select__label"}>
                                            {t('label.ini_date') + " *"}</label>
                                        <Datetime
                                            locale={getLocale()}
                                            dateFormat={getStringFormatDate()}
                                            timeFormat={false}
                                            value={dtini}
                                            onChange={date => setdtini(date)}
                                            inputProps={{ name: "dtini", readOnly: 'readonly' }}
                                            className={filterErrors.dtini ? "error" : ""}
                                        />
                                        {filterErrors.dtini && <FormHelperText error>
                                            <span>{filterErrors.dtini}</span>
                                        </FormHelperText>}
                                    </FormControl>
                                </GridItem>
                                <GridItem xs={12} sm={6} md={2}>
                                    <FormControl fullWidth className={classes.formControl}>
                                        <label className={filterErrors.dtfim ? "select__label select__label_error" : "select__label"}>
                                            {t('label.end_date') + " *"}</label>
                                        <Datetime
                                            locale={getLocale()}
                                            dateFormat={getStringFormatDate()}
                                            timeFormat={false}
                                            value={dtfim}
                                            onChange={date => setdtfim(date)}
                                            inputProps={{ name: "dtfim", readOnly: 'readonly' }}
                                            className={filterErrors.dtfim ? "error" : ""}
                                        />
                                        {filterErrors.dtfim && <FormHelperText error>
                                            <span>{filterErrors.dtfim}</span>
                                        </FormHelperText>}
                                    </FormControl>
                                </GridItem>
                                <GridItem xs={12} sm={6} md={3}>
                                    <FormControl fullWidth className={classes.formControl}>
                                        <label className="select__label">{t('notice_board.type')}</label>
                                        <Select
                                            classNamePrefix="select"
                                            styles={ValidSelect(false)}
                                            isClearable={true}
                                            isSearchable={false}
                                            name="tipo"
                                            placeholder={t('label.all')}
                                            options={tipoOptions}
                                            value={tipoOptions.filter(o => o.value === tipo)}
                                            onChange={(opt) => setTipo(opt ? opt.value : null)}
                                        />
                                    </FormControl>
                                </GridItem>
                                <GridItem xs={12} sm={6} md={3}>
                                    <FormControl fullWidth className={classes.formControl}>
                                        <label className="select__label">{t('notice_board.situation')}</label>
                                        <Select
                                            classNamePrefix="select"
                                            styles={ValidSelect(false)}
                                            isClearable={true}
                                            isSearchable={false}
                                            name="situacao"
                                            placeholder={t('label.all')}
                                            options={situacaoOptions}
                                            value={situacaoOptions.filter(o => o.value === situacao)}
                                            onChange={(opt) => setSituacao(opt ? opt.value : null)}
                                        />
                                    </FormControl>
                                </GridItem>
                                <GridItem xs={12} sm={6} md={2}>
                                    <Button round color="primary" disabled={isLoadingList}
                                        onClick={handleClickPesquisa}>
                                        {t('label.search')}
                                    </Button>
                                </GridItem>
                            </GridContainer>
                            <Button
                                color="primary"
                                className={classes.newButton}
                                onClick={handleNovo}
                            >
                                {t('notice_board.new_notice')}
                            </Button>
                            <br />
                            <ReactTable
                                loading={isLoadingList}
                                data={list}
                                filterable
                                defaultFilterMethod={noCaseSensitive}
                                columns={columns}
                                defaultSorted={[{ id: "dataCriacao", desc: true }]}
                                defaultPageSize={10}
                                minRows={5}
                                showPaginationBottom={true}
                                className="-striped -highlight"
                                previousText={t('button.previous')}
                                nextText={t('button.next')}
                                loadingText={t('label.loading') + "..."}
                                noDataText={isErrorList
                                    ? t('notice_board.load_error')
                                    : t('information_message.no_records_found') + "!"}
                                pageText={t('label.page')}
                                ofText={t('label.of')}
                                rowsText={t('label.rows')}
                            />
                        </CardBody>
                    </Card>
                </GridItem>
            </GridContainer>

            {renderAlertInativar()}
        </div>
    );
}
